// Errores de fijar las piezas de la venta (/pieces) → mensaje y acción del vendedor.
import { ApiError } from '@/api/errors';
import { wizardErrorMsg } from './api/wizardError';
import { lockedImeiMessage } from './deviceErrors';
import { identifierMismatchOf, lineErrorMessage } from './lineErrors';

/**
 * `rescan` = escanear otra pieza (o la ya registrada) · `fix-capture` = corregir lo capturado ·
 * `reload` = releer la venta · `support` = no reintentar y reportar · `retry` = mismo body otra vez.
 */
export type PinPiecesAction = 'rescan' | 'fix-capture' | 'reload' | 'support' | 'retry';

export interface PinPiecesFailure {
    message: string;
    action: PinPiecesAction;
    /** Línea y SKU de la pieza rechazada, cuando el silo los manda. */
    target?: { lineId: string; sku: string };
}

const ACTION_BY_CODE: Record<string, PinPiecesAction> = {
    LINE_NOT_FOUND: 'reload',
    INVALID_SESSION_STATE: 'reload',
    LINE_SKU_MISMATCH: 'fix-capture',
    VALIDATION_ERROR: 'fix-capture',
    IDENTIFIER_NOT_AVAILABLE: 'rescan',
    IDENTIFIER_MISMATCH: 'rescan',
    IMEI_CHANGE_NOT_SUPPORTED: 'rescan',
    DEVICE_ALREADY_ENROLLED: 'rescan',
    RESERVATION_NOT_FOUND: 'support',
    CATALOG_UNAVAILABLE: 'retry',
    FINANCED_IMEI_LOCKED: 'support',
};

const MESSAGE_BY_CODE: Record<string, string> = {
    INVALID_SESSION_STATE:
        'Esta venta ya cerró o se canceló. Recarga la venta para ver cómo quedó.',
    VALIDATION_ERROR:
        'Revisa lo capturado: cada pieza lleva su propio IMEI o serie, sin repetir, y el IMEI tiene 15 dígitos.',
    RESERVATION_NOT_FOUND:
        'Lo apartado de este producto ya no está en el inventario. No lo vuelvas a intentar: repórtalo a soporte para que revisen el inventario.',
    CATALOG_UNAVAILABLE:
        'El inventario no respondió. Las piezas que ya quedaron fijadas se conservan; vuelve a intentar en un momento.',
    FINANCED_IMEI_LOCKED:
        'El IMEI ya va en el contrato. Si está equivocado, cancela la venta y vuelve a levantarla.',
};

const FALLBACK = 'No se pudieron fijar las piezas de la venta.';

/** El fallo se arregla en la captura: escanear otra pieza o corregir lo tecleado. */
export function pinFailureAboutPieces(failure: PinPiecesFailure): boolean {
    return failure.action === 'rescan' || failure.action === 'fix-capture';
}

/** Mensaje y acción de un fallo de /pieces. */
export function pinPiecesFailure(error: unknown): PinPiecesFailure {
    if (!(error instanceof ApiError)) {
        return { message: wizardErrorMsg(error, FALLBACK), action: 'retry' };
    }
    const known = Object.hasOwn(ACTION_BY_CODE, error.code)
        ? ACTION_BY_CODE[error.code]
        : undefined;
    const action = known ?? (error.httpStatus >= 500 ? 'retry' : 'reload');
    const message =
        (Object.hasOwn(MESSAGE_BY_CODE, error.code) ? MESSAGE_BY_CODE[error.code] : undefined) ??
        lockedImeiMessage(error) ??
        lineErrorMessage(error) ??
        wizardErrorMsg(error, FALLBACK);
    const target = identifierMismatchOf(error);
    return target !== undefined ? { message, action, target } : { message, action };
}
